import React, { useState } from 'react';
import { Upload, FileCode, X, Check, AlertCircle, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * InteractiveHtmlUploader - Drag & drop uploader for complete HTML articles
 * 
 * Features:
 * - Drag and drop HTML file upload
 * - Preview before publishing
 * - Auto-extracts title, meta description
 * - Preserves all CSS, JavaScript, and embedded assets
 * - Creates article entry in database with redirect to standalone page
 */
const InteractiveHtmlUploader = ({ onSuccess }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [htmlContent, setHtmlContent] = useState('');
  const [extractedMeta, setExtractedMeta] = useState({
    title: '',
    excerpt: '',
    author: 'SheLovesThee Team',
    category: 'HBCU',
    slug: ''
  });
  const [isUploading, setIsUploading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');

  const categories = ['HBCU', 'SWAC', 'Jackson State', 'Football', 'Basketball', 'Recruiting', 'Soccer', 'Track & Field'];

  // Handle drag events
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Generate URL-safe slug from title
  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .substring(0, 50);
  };

  // Extract metadata from HTML
  const extractMetadata = (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Extract title
    const titleTag = doc.querySelector('title');
    const h1Tag = doc.querySelector('h1');
    const ogTitle = doc.querySelector('meta[property="og:title"]');
    const title = ogTitle?.getAttribute('content') || titleTag?.textContent || h1Tag?.textContent || '';

    // Extract description/excerpt
    const metaDesc = doc.querySelector('meta[name="description"]');
    const ogDesc = doc.querySelector('meta[property="og:description"]');
    const excerpt = ogDesc?.getAttribute('content') || metaDesc?.getAttribute('content') || '';

    // Extract author
    const authorMeta = doc.querySelector('meta[name="author"]');
    const author = authorMeta?.getAttribute('content') || 'SheLovesThee Team';

    // Extract image for social card
    const ogImage = doc.querySelector('meta[property="og:image"]');
    const firstImg = doc.querySelector('img');
    const imageUrl = ogImage?.getAttribute('content') || firstImg?.getAttribute('src') || '';

    return {
      title: title.trim(),
      excerpt: excerpt.trim().substring(0, 300),
      author: author.trim(),
      slug: generateSlug(title),
      imageUrl
    };
  };

  // Process the uploaded file
  const processFile = (file) => {
    if (!file.name.endsWith('.html') && !file.name.endsWith('.htm')) {
      toast.error('Please upload an HTML file (.html or .htm)');
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      toast.error('File size should be less than 10MB');
      return;
    }

    setUploadedFile(file);

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target.result;
      setHtmlContent(content);

      // Extract metadata
      const meta = extractMetadata(content);
      setExtractedMeta(prev => ({
        ...prev,
        ...meta
      }));

      toast.success('HTML file loaded! Review metadata below.');
    };
    reader.onerror = () => {
      toast.error('Failed to read file');
      setUploadedFile(null);
    };
    reader.readAsText(file);
  };

  // Handle file drop
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      processFile(files[0]);
    }
  };

  // Handle file input change
  const handleFileInput = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  // Clear uploaded file
  const clearFile = () => {
    setUploadedFile(null);
    setHtmlContent('');
    setExtractedMeta({
      title: '',
      excerpt: '',
      author: 'SheLovesThee Team',
      category: 'HBCU',
      slug: ''
    });
    setScheduledDate('');
  };

  // Upload and publish article
  const handlePublish = async () => {
    if (!htmlContent || !extractedMeta.title || !extractedMeta.slug) {
      toast.error('Please fill in title and URL slug');
      return;
    }

    setIsUploading(true);

    try {
      // Upload the HTML file to create a standalone article
      const response = await axios.post(`${API}/articles/interactive`, {
        title: extractedMeta.title,
        excerpt: extractedMeta.excerpt,
        author: extractedMeta.author,
        category: extractedMeta.category,
        slug: extractedMeta.slug,
        html_content: htmlContent,
        scheduled_publish_at: scheduledDate || null
      });

      if (response.data.success) {
        toast.success(scheduledDate 
          ? `Article scheduled for ${new Date(scheduledDate).toLocaleString()}!` 
          : 'Interactive article published successfully!'
        );
        clearFile();
        if (onSuccess) onSuccess();
      }
    } catch (error) {
      console.error('Error publishing article:', error);
      toast.error(error.response?.data?.detail || 'Failed to publish article');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="bg-white p-8 rounded-lg shadow-lg space-y-6" data-testid="html-uploader">
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-purple-100 p-3 rounded-full">
          <FileCode className="h-6 w-6 text-purple-600" />
        </div>
        <div>
          <h2 className="text-2xl font-bold uppercase heading-font text-[#001f3f]">
            Interactive Article Uploader
          </h2>
          <p className="text-slate-600 text-sm">
            Upload complete HTML articles with embedded CSS, JavaScript, and images
          </p>
        </div>
      </div>

      {/* Drag & Drop Zone */}
      {!uploadedFile && (
        <div
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className={`
            border-2 border-dashed rounded-xl p-12 text-center transition-all cursor-pointer
            ${isDragging 
              ? 'border-purple-500 bg-purple-50' 
              : 'border-slate-300 hover:border-purple-400 hover:bg-slate-50'
            }
          `}
          data-testid="drop-zone"
        >
          <Upload className={`h-16 w-16 mx-auto mb-4 ${isDragging ? 'text-purple-500' : 'text-slate-400'}`} />
          <p className="text-lg font-semibold text-slate-700 mb-2">
            {isDragging ? 'Drop your HTML file here!' : 'Drag & drop your HTML article'}
          </p>
          <p className="text-slate-500 mb-4">or</p>
          <label className="inline-block">
            <input
              type="file"
              accept=".html,.htm"
              onChange={handleFileInput}
              className="hidden"
              data-testid="file-input"
            />
            <span className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-full font-semibold cursor-pointer transition-colors">
              Browse Files
            </span>
          </label>
          <p className="text-slate-400 text-sm mt-4">
            Supports .html and .htm files up to 10MB
          </p>
        </div>
      )}

      {/* Uploaded File Info */}
      {uploadedFile && (
        <div className="space-y-6">
          {/* File Badge */}
          <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <Check className="h-5 w-5 text-green-600" />
              <div>
                <p className="font-semibold text-green-800">{uploadedFile.name}</p>
                <p className="text-sm text-green-600">
                  {(uploadedFile.size / 1024).toFixed(1)} KB
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowPreview(!showPreview)}
                className="text-purple-600 border-purple-300"
              >
                <Eye className="h-4 w-4 mr-1" />
                {showPreview ? 'Hide Preview' : 'Preview'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={clearFile}
                className="text-red-600 hover:bg-red-50"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Preview iframe */}
          {showPreview && (
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="bg-slate-100 px-4 py-2 border-b">
                <p className="text-sm font-medium text-slate-600">Article Preview</p>
              </div>
              <iframe
                srcDoc={htmlContent}
                title="Article Preview"
                className="w-full h-96 border-0"
                sandbox="allow-scripts"
              />
            </div>
          )}

          {/* Metadata Form */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-bold uppercase tracking-wider text-slate-700 mb-2">
                Title *
              </label>
              <input
                type="text"
                value={extractedMeta.title}
                onChange={(e) => setExtractedMeta(prev => ({ 
                  ...prev, 
                  title: e.target.value,
                  slug: generateSlug(e.target.value)
                }))}
                className="w-full p-3 border border-slate-300 rounded-md focus:ring-2 focus:ring-purple-500"
                placeholder="Article title"
                required
                data-testid="meta-title"
              />
            </div>

            <div>
              <label className="block text-sm font-bold uppercase tracking-wider text-slate-700 mb-2">
                URL Slug *
              </label>
              <div className="flex items-center">
                <span className="bg-slate-100 px-3 py-3 border border-r-0 border-slate-300 rounded-l-md text-slate-500 text-sm">
                  /
                </span>
                <input
                  type="text"
                  value={extractedMeta.slug}
                  onChange={(e) => setExtractedMeta(prev => ({ ...prev, slug: e.target.value }))}
                  className="flex-1 p-3 border border-slate-300 rounded-r-md focus:ring-2 focus:ring-purple-500"
                  placeholder="article-url-slug"
                  required
                  data-testid="meta-slug"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold uppercase tracking-wider text-slate-700 mb-2">
                Author
              </label>
              <input
                type="text"
                value={extractedMeta.author}
                onChange={(e) => setExtractedMeta(prev => ({ ...prev, author: e.target.value }))}
                className="w-full p-3 border border-slate-300 rounded-md focus:ring-2 focus:ring-purple-500"
                placeholder="Author name"
                data-testid="meta-author"
              />
            </div>

            <div>
              <label className="block text-sm font-bold uppercase tracking-wider text-slate-700 mb-2">
                Category
              </label>
              <select
                value={extractedMeta.category}
                onChange={(e) => setExtractedMeta(prev => ({ ...prev, category: e.target.value }))}
                className="w-full p-3 border border-slate-300 rounded-md focus:ring-2 focus:ring-purple-500"
                data-testid="meta-category"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-bold uppercase tracking-wider text-slate-700 mb-2">
                Excerpt / Description
              </label>
              <textarea
                value={extractedMeta.excerpt}
                onChange={(e) => setExtractedMeta(prev => ({ ...prev, excerpt: e.target.value }))}
                className="w-full p-3 border border-slate-300 rounded-md focus:ring-2 focus:ring-purple-500"
                rows={3}
                placeholder="Brief description for social sharing and article listings"
                data-testid="meta-excerpt"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-bold uppercase tracking-wider text-slate-700 mb-2">
                <span className="flex items-center gap-2">
                  Schedule Publication (Optional)
                  <span className="text-xs font-normal text-slate-500 normal-case">
                    Leave empty to publish immediately
                  </span>
                </span>
              </label>
              <input
                type="datetime-local"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
                className="w-full p-3 border border-slate-300 rounded-md focus:ring-2 focus:ring-purple-500"
                data-testid="meta-schedule"
              />
            </div>
          </div>

          {/* Info Box */}
          <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-semibold">How it works:</p>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>Your HTML file will be saved as a standalone article page</li>
                <li>All CSS, JavaScript, and embedded images are preserved</li>
                <li>The article will appear in the News listing and can be featured</li>
                <li>URL: <code className="bg-blue-100 px-1 rounded">/{extractedMeta.slug || 'your-article-slug'}</code></li>
              </ul>
            </div>
          </div>

          {/* Publish Button */}
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={clearFile}
              className="px-6"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handlePublish}
              disabled={isUploading || !extractedMeta.title || !extractedMeta.slug}
              className="bg-purple-600 hover:bg-purple-700 text-white px-8"
              data-testid="publish-btn"
            >
              {isUploading ? (
                <>Publishing...</>
              ) : scheduledDate ? (
                <>Schedule Article</>
              ) : (
                <>Publish Now</>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InteractiveHtmlUploader;
